import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Container, Spinner } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import { LuGraduationCap } from 'react-icons/lu'
import { BsArrowClockwise } from 'react-icons/bs'
import { Navigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import { useBusJourneyTracking } from './trackVehicle/useBusJourneyTracking'
import { isBusAttendantUser } from '../utils/busAttendant'

const dash = (v) =>
  v === null || v === undefined || String(v).trim() === '' ? '—' : String(v)

/** Normalize GET /transport/my-assigned-bus body: null, single bus object, or array. */
function normalizeMyAssignedBuses(apiResponseBody) {
  const raw = apiResponseBody?.data ?? apiResponseBody
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'object' && (raw.bus_id != null || raw.id != null)) return [raw]
  return []
}

function resolveTransportBusId(bus) {
  const raw = bus?.bus_id ?? bus?.id
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

function journeyStatusFromBus(bus) {
  const st = bus?.bus_journey_status ?? bus?.busJourneyStatus
  if (st == null || st === '') return null
  return String(st).toLowerCase()
}

/** Background GPS ping while a journey is live (manual start already sends the first fix). */
const LIVE_LOCATION_INTERVAL_MS = 20000

export default function AttendantPortal() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()

  /** Rows from GET /transport/my-assigned-bus (already scoped to this attendant). */
  const [buses, setBuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [listLoadFailed, setListLoadFailed] = useState(false)

  const { startOrUpdateLocation, completeJourney, inFlight } = useBusJourneyTracking()

  const loadMyBuses = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) {
      setLoading(true)
      setListLoadFailed(false)
    }
    let list = []
    try {
      const res = await ApiEndpoints.getMyAssignedBus()
      list = normalizeMyAssignedBuses(res.data)
      setBuses(list)
    } catch (error) {
      list = []
      if (showSpinner) {
        setBuses([])
        setListLoadFailed(true)
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Could not load your assigned vehicles.',
        )
      }
    } finally {
      if (showSpinner) setLoading(false)
    }
    return list
  }, [])

  useEffect(() => {
    loadMyBuses()
  }, [loadMyBuses])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  /** Backend: only one assigned bus may have status `started` at a time (409 otherwise). */
  const liveBusId = useMemo(() => {
    for (const b of buses) {
      const id = resolveTransportBusId(b)
      if (!id) continue
      if (journeyStatusFromBus(b) === 'started') return id
    }
    return null
  }, [buses])

  useEffect(() => {
    if (liveBusId == null) return
    const id = liveBusId
    let cancelled = false
    const busyRef = { current: false }
    const tick = async () => {
      if (cancelled || busyRef.current) return
      busyRef.current = true
      try {
        await startOrUpdateLocation(id, { silent: true })
      } catch {
        /* no toast — background ping */
      } finally {
        busyRef.current = false
      }
    }
    const t = window.setInterval(tick, LIVE_LOCATION_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(t)
    }
  }, [liveBusId, startOrUpdateLocation])

  const handleStartOrUpdateJourney = useCallback(
    async (busId) => {
      try {
        await startOrUpdateLocation(busId)
        await loadMyBuses({ showSpinner: false })
      } catch {
        /* toast in hook */
      }
    },
    [startOrUpdateLocation, loadMyBuses],
  )

  const handleCompleteJourney = useCallback(
    async (busId) => {
      try {
        await completeJourney(busId)
        await loadMyBuses({ showSpinner: false })
      } catch {
        /* toast in hook */
      }
    },
    [completeJourney, loadMyBuses],
  )

  if (!user) {
    return <Navigate to="/LoginSignup" replace />
  }

  if (!isBusAttendantUser(user)) {
    return <Navigate to="/Dashboard" replace />
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: isMobile ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px',
              }}
            >
              <div
                className="d-flex align-items-center gap-3"
                style={{ flex: 1, minWidth: 0 }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title text-start"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2',
                    }}
                  >
                    Attendant portal
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}
                  >Only one assigned bus can be live at a time—
                    complete that journey before starting another.
                  </p>
                </div>
              </div>
              {!isMobile && (
                <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                  <ProfileDropdown
                    logout={logout}
                    notificationCount={3}
                    showNotifications={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="p-0 mt-4"
          style={{
            minHeight: '60vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <Card
            className="border-0 shadow-sm mb-4"
            style={{
              borderRadius: '12px',
              border: '1px solid #D1D3D4',
              overflow: 'hidden',
              backgroundColor: '#ffffff',
            }}
          >
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #F3F4F6',
                background: '#ffffff',
              }}
            >
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-3">
                <button
                  type="button"
                  onClick={() => loadMyBuses({ showSpinner: true })}
                  disabled={loading}
                  title="Refresh assigned buses"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: loading ? '#9ca3af' : '#223843',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <BsArrowClockwise size={20} />
                </button>
              </div>
            </div>
            <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
              <Card
                className="table-card m-0"
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  overflow: 'visible',
                  backgroundColor: '#ffffff',
                  position: 'relative',
                }}
              >
                <div
                  className="table-wrapper"
                  style={{
                    overflowX: 'auto',
                    overflowY: 'visible',
                    position: 'relative',
                    borderRadius: '12px',
                  }}
                >
                  {loading ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center py-5 gap-2"
                      style={{ minHeight: '200px' }}
                    >
                      <Spinner animation="border" variant="primary" />
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>Loading your buses…</span>
                    </div>
                  ) : (
                    <Table className="table mb-0" style={{ marginBottom: 0 }}>
                      <Thead>
                        <Tr style={{ backgroundColor: '#F3F4F6' }}>
                          {[
                            'Bus number',
                            'Driver',
                            'Driver contact',
                            'Owner',
                            'Journey',
                            'Action',
                          ].map((h) => (
                            <Th
                              key={h}
                              style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                padding: '16px',
                                border: 'none',
                                borderBottom: '1px solid #D1D3D4',
                                textAlign: 'left',
                                color: '#111827',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {h}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {buses.length === 0 ? (
                          <Tr>
                            <Td
                              colSpan={6}
                              style={{
                                textAlign: 'center',
                                padding: '24px',
                                color: '#6b7280',
                                fontSize: '14px',
                              }}
                            >
                              {listLoadFailed
                                ? 'Could not load your assigned buses. Try refresh or sign in again.'
                                : 'No vehicle is assigned to your account yet. Ask an administrator to assign you on a bus.'}
                            </Td>
                          </Tr>
                        ) : (
                          buses.map((bus, index) => {
                            const busId = resolveTransportBusId(bus)
                            const busy = inFlight[busId]
                            const status = journeyStatusFromBus(bus)
                            const isLive = status === 'started'
                            const anotherLive =
                              liveBusId != null && liveBusId !== busId
                            const canStartHere = !anotherLive && !isLive
                            return (
                              <Tr
                                key={String(resolveTransportBusId(bus) ?? index)}
                                style={{
                                  backgroundColor:
                                    index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                                  transition: 'background-color 0.2s ease',
                                  borderBottom: '1px solid #E5E7EB',
                                  textAlign: 'left',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    'rgba(37, 99, 235, 0.06)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                                }}
                              >
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(bus?.bus_number)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(bus?.driver_name)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(bus?.driver_contact_number)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(bus?.bus_owner_name)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  {isLive ? (
                                    <Badge bg="success" pill className="fw-semibold">
                                      Live
                                    </Badge>
                                  ) : status === 'completed' || status === 'complete' ? (
                                    <Badge bg="secondary" pill className="fw-semibold">
                                      Completed
                                    </Badge>
                                  ) : (
                                    <Badge bg="light" text="dark" pill className="fw-semibold border">
                                      Idle
                                    </Badge>
                                  )}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  <div className="d-flex flex-wrap gap-2 align-items-center">
                                    <Button
                                      type="button"
                                      variant="primary"
                                      size="sm"
                                      className="fw-semibold"
                                      title={
                                        anotherLive
                                          ? 'Complete the journey on your other assigned bus first (API returns 409).'
                                          : undefined
                                      }
                                      style={{
                                        backgroundColor: '#223843',
                                        borderColor: '#223843',
                                        borderRadius: '8px',
                                        padding: '8px 14px',
                                      }}
                                      disabled={
                                        !Number.isFinite(busId) ||
                                        busId <= 0 ||
                                        busy === 'start' ||
                                        busy === 'complete' ||
                                        (!isLive && !canStartHere)
                                      }
                                      onClick={() => handleStartOrUpdateJourney(busId)}
                                    >
                                      {busy === 'start'
                                        ? 'Sending…'
                                        : isLive
                                          ? 'Update location'
                                          : 'Start journey'}
                                    </Button>
                                    {isLive ? (
                                      <Button
                                        type="button"
                                        variant="outline-secondary"
                                        size="sm"
                                        className="fw-semibold"
                                        style={{ borderRadius: '8px', padding: '8px 14px' }}
                                        disabled={busy === 'start' || busy === 'complete'}
                                        onClick={() => handleCompleteJourney(busId)}
                                      >
                                        {busy === 'complete' ? 'Completing…' : 'Complete journey'}
                                      </Button>
                                    ) : null}
                                  </div>
                                </Td>
                              </Tr>
                            )
                          })
                        )}
                      </Tbody>
                    </Table>
                  )}
                </div>
              </Card>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  )
}
